import { useState } from "react";
import { Link } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import Breadcrumbs from "@/components/base/Breadcrumbs";
import { FAQS } from "@/mocks/comics";
import { usePageMeta } from "@/hooks/usePageMeta";

const VALUES = [
  { icon: "ri-stack-line", title: "结构化目录", desc: "把零散作品信息整理成可检索、可筛选的结构化条目。" },
  { icon: "ri-flashlight-line", title: "三步内找到", desc: "焦点推荐、分类筛选、关键词搜索，路径始终清晰。" },
  { icon: "ri-shield-star-line", title: "尊重版权", desc: "内容以介绍与索引为主，倡导正版购买与阅读。" },
];

const TEAM = [
  { name: "编辑部 · 目录组", role: "作品信息与章节索引", desc: "负责作品简介校对、章节目录与资源格式说明整理。" },
  { name: "编辑部 · 视觉组", role: "封面与页面体验", desc: "负责封面筛选、页面视觉与移动端阅读体验优化。" },
  { name: "技术组 · 数据接口", role: "检索与分发链路", desc: "负责搜索、筛选逻辑与后续资源分发接口接入。" },
];

export default function About() {
  const [openFaq, setOpenFaq] = useState<number | null>(0);

  usePageMeta(
    "关于我们 - 禁漫天堂",
    "禁漫天堂是面向动漫漫画爱好者的资源聚合站点，了解我们的定位、编辑团队构成、联系方式与常见问题说明。",
    "禁漫天堂,关于我们,漫画资源站,联系方式,常见问题",
  );

  return (
    <SiteLayout>
      <div className="w-full px-4 md:px-6 pt-6 md:pt-8">
        <Breadcrumbs items={[{ label: "首页", to: "/" }, { label: "关于我们" }]} />

        <section className="mt-4 rounded-lg border hairline bg-gradient-to-r from-primary-900/70 via-background-900/80 to-secondary-900/60 px-5 md:px-9 py-8 md:py-12">
          <p className="text-[11px] font-label tracking-[0.24em] text-accent-400">ABOUT MANGA HAVEN</p>
          <h1 className="mt-3 font-heading text-2xl md:text-4xl font-bold leading-tight text-foreground-50 max-w-[780px]">
            做漫画阅读路径的组织者
          </h1>
          <p className="mt-4 text-sm md:text-[15px] leading-7 text-foreground-300 max-w-[760px]">
            禁漫天堂面向各年龄段的动漫漫画爱好者，尤其是希望快速找到最新、丰富、高质量漫画资源的用户群体。我们把作品介绍、章节目录、资源格式与题材分类整理成清晰的目录体系，让每一次查找都更短、更准确。
          </p>
          <div className="mt-6 grid grid-cols-3 max-w-[520px] divide-x divide-background-700/60 rounded-lg border hairline bg-background-950/50 overflow-hidden">
            {[
              { v: "18+", l: "收录作品" },
              { v: "8", l: "题材分类" },
              { v: "1,800+", l: "章节索引" },
            ].map((item) => (
              <div key={item.l} className="px-3 md:px-4 py-3">
                <p className="font-heading text-lg md:text-xl font-semibold text-accent-300">{item.v}</p>
                <p className="text-[11px] text-foreground-500 mt-1">{item.l}</p>
              </div>
            ))}
          </div>
        </section>

        <section aria-labelledby="values" className="mt-8">
          <h2 id="values" className="font-heading text-xl md:text-2xl font-semibold text-foreground-50 mb-4">我们的坚持</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-4">
            {VALUES.map((item) => (
              <div key={item.title} className="card-lift rounded-lg border hairline bg-background-900/60 p-5">
                <span className="w-10 h-10 rounded-md flex items-center justify-center bg-primary-800/70 text-accent-300">
                  <i className={`${item.icon} text-lg`} />
                </span>
                <h3 className="mt-3 text-sm md:text-[15px] font-medium text-foreground-50">{item.title}</h3>
                <p className="mt-1.5 text-xs leading-6 text-foreground-400">{item.desc}</p>
              </div>
            ))}
          </div>
        </section>

        <section aria-labelledby="team" className="mt-8">
          <h2 id="team" className="font-heading text-xl md:text-2xl font-semibold text-foreground-50 mb-4">团队介绍</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-4">
            {TEAM.map((member) => (
              <div key={member.name} className="rounded-lg border hairline bg-background-900/60 p-5">
                <div className="flex items-center gap-2.5">
                  <span className="w-9 h-9 rounded-md flex items-center justify-center bg-secondary-900/70 text-secondary-200">
                    <i className="ri-team-line text-base" />
                  </span>
                  <div className="min-w-0">
                    <p className="text-[13px] text-foreground-100 truncate">{member.name}</p>
                    <p className="text-[11px] text-foreground-500 truncate">{member.role}</p>
                  </div>
                </div>
                <p className="mt-3 text-xs leading-6 text-foreground-400">{member.desc}</p>
              </div>
            ))}
          </div>
        </section>

        <section aria-labelledby="contact" className="mt-8 grid grid-cols-1 lg:grid-cols-[1fr_1fr] gap-4">
          <div className="rounded-lg border hairline bg-background-900/60 p-5 md:p-6">
            <h2 id="contact" className="font-heading text-lg md:text-xl font-semibold text-foreground-50">联系方式</h2>
            <ul className="mt-4 space-y-3 text-sm">
              {[
                { icon: "ri-mail-line", label: "合作与反馈", value: "support@jinman-haven.com" },
                { icon: "ri-customer-service-2-line", label: "客服值班", value: "每日 09:00 - 23:00" },
                { icon: "ri-copyright-line", label: "版权投诉", value: "copyright@jinman-haven.com" },
                { icon: "ri-map-pin-line", label: "运营团队", value: "线上运营 · 远程协作" },
              ].map((row) => (
                <li key={row.label} className="flex items-start gap-3">
                  <span className="w-8 h-8 rounded flex items-center justify-center bg-background-800 text-accent-300 shrink-0">
                    <i className={`${row.icon} text-sm`} />
                  </span>
                  <span className="min-w-0">
                    <span className="block text-[11px] text-foreground-500">{row.label}</span>
                    <span className="block text-[13px] text-foreground-200 mt-0.5 break-all">{row.value}</span>
                  </span>
                </li>
              ))}
            </ul>
            <div className="mt-5 flex flex-wrap gap-2">
              <Link
                to="/search"
                className="h-9 px-4 rounded-md bg-accent-500 text-background-950 text-xs font-medium flex items-center gap-1.5 hover:bg-accent-400 transition-colors whitespace-nowrap"
              >
                去搜索漫画
              </Link>
              <Link
                to="/category/hot-blood"
                className="h-9 px-4 rounded-md border hairline text-xs text-foreground-200 flex items-center gap-1.5 hover:border-accent-500/50 hover:text-accent-300 transition-colors whitespace-nowrap"
              >
                浏览分类
              </Link>
            </div>
          </div>

          <div className="rounded-lg border hairline bg-background-900/60 p-5 md:p-6">
            <h2 className="font-heading text-lg md:text-xl font-semibold text-foreground-50">使用说明</h2>
            <ol className="mt-4 space-y-3">
              {[
                "首页顶部或导航栏搜索框输入关键词，回车即可进入搜索结果页。",
                "分类页可按状态、地区、年份与排序组合筛选，无结果时一键重置。",
                "详情页提供章节索引与资源格式说明，下载与分享为演示交互。",
                "移动端导航收起为汉堡菜单，筛选条件在窄屏下自动换行排列。",
              ].map((step, i) => (
                <li key={step} className="flex items-start gap-3">
                  <span className="w-6 h-6 rounded flex items-center justify-center text-[11px] font-label bg-primary-800/70 text-accent-300 shrink-0">
                    {i + 1}
                  </span>
                  <span className="text-xs leading-6 text-foreground-300">{step}</span>
                </li>
              ))}
            </ol>
          </div>
        </section>

        <section id="faq" aria-labelledby="faq-title" className="mt-8">
          <h2 id="faq-title" className="font-heading text-xl md:text-2xl font-semibold text-foreground-50 mb-4">常见问题</h2>
          <div className="rounded-lg border hairline bg-background-900/60 divide-y divide-background-700/50 overflow-hidden">
            {FAQS.map((faq, index) => (
              <div key={faq.q}>
                <button
                  type="button"
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                  aria-expanded={openFaq === index}
                  className="w-full flex items-center justify-between gap-3 px-4 md:px-5 py-4 text-left hover:bg-background-800/40 transition-colors cursor-pointer"
                >
                  <span className="text-[13px] md:text-sm text-foreground-100">{faq.q}</span>
                  <i className={`ri-add-line text-foreground-400 shrink-0 transition-transform ${openFaq === index ? "rotate-45" : ""}`} />
                </button>
                {openFaq === index && (
                  <p className="px-4 md:px-5 pb-4 text-xs leading-6 text-foreground-400 animate-fade-in">{faq.a}</p>
                )}
              </div>
            ))}
          </div>
        </section>

        <div className="mt-8 rounded-lg border hairline bg-background-900/50 px-4 py-3">
          <p className="text-[11px] leading-5 text-foreground-500">
            版权声明：本站展示的作品名称、简介与封面仅用于页面演示与作品索引，版权归原作者及出版方所有。若权利方认为内容存在不当展示，可通过上方版权投诉邮箱联系我们处理。
          </p>
        </div>
      </div>
      <div className="h-12" />
    </SiteLayout>
  );
}